import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { commonMessages } from '@/messages/common';

import { RetailerLanguageSwitcher } from '../RetailerLanguageSwitcher';

import * as S from './RetailerNavbar.styles';

interface RetailerWidgetConfig {
  countryCode: string;
  defaultLocale?: string;
  enabled?: boolean;
  locales?: string[];
  url?: string;
}

interface RetailerNavbarProps {
  activeTab: 'selection' | 'new';
  backButtonText?: string;
  onBackClick?: () => void;
  onTabChange: (tab: 'selection' | 'new') => void;
  showBackButton?: boolean;
  widgetConfig?: RetailerWidgetConfig;
}

export const RetailerNavbar: FC<RetailerNavbarProps> = ({
  activeTab,
  onBackClick,
  onTabChange,
  showBackButton = false,
  widgetConfig,
}) => {
  const { formatMessage } = useIntl();

  // Always show the language switcher if there are locales available
  const showLanguageSwitcher =
    widgetConfig?.enabled &&
    widgetConfig?.locales &&
    widgetConfig.locales.length > 0;

  return (
    <S.NavbarContainer>
      <S.TabList>
        {showBackButton && (
          <S.TabItem isActive={true}>
            <S.BackButton
              aria-label={formatMessage(commonMessages.backToList)}
              onClick={onBackClick}
              type='button'
            >
              {formatMessage(commonMessages.backToList)}
            </S.BackButton>
          </S.TabItem>
        )}
        <S.TabItem isActive={!showBackButton && activeTab === 'selection'}>
          <S.TabButton
            aria-selected={!showBackButton && activeTab === 'selection'}
            onClick={() => onTabChange('selection')}
          >
            {formatMessage(commonMessages.ourSelection)}
          </S.TabButton>
        </S.TabItem>
        <S.TabItem isActive={!showBackButton && activeTab === 'new'}>
          <S.TabButton
            aria-selected={!showBackButton && activeTab === 'new'}
            onClick={() => onTabChange('new')}
          >
            {formatMessage(commonMessages.newWatches)}
          </S.TabButton>
        </S.TabItem>
      </S.TabList>

      {showLanguageSwitcher && (
        <RetailerLanguageSwitcher
          countryCode={widgetConfig.countryCode}
          defaultLocale={widgetConfig.defaultLocale || 'EN'}
          locales={widgetConfig.locales || []}
        />
      )}
    </S.NavbarContainer>
  );
};
